import { useState } from 'react'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import PDFViewerDialog from './PDFViewerDialog'
import SourceUploadDialog from './SourceUploadDialog'

import type { BuktiKaryaDocumentResponse } from 'types/bukti-karya'
import type {
  BuktiDukungFileInstance,
  KinerjaRealisasiState,
} from 'types/kinerja'

interface FileUploadProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange' | 'onError'> {
  file: BuktiDukungFileInstance
  url?: string
  fileName?: string
  description?: string
  realizationState?: KinerjaRealisasiState
  isUploading?: boolean
  isDisabled?: boolean
  onChange?: (file: File | BuktiKaryaDocumentResponse) => void
  onError?: (errorMessage: string) => void
  onOpenFile?: () => void
}

export default function FileUpload({
  file: buktiDukungFile,
  description = '',
  isDisabled = false,
  isUploading = false,
  realizationState = 'DRAFT',
  onChange = () => {},
  onError = () => {},
  onOpenFile,
  ...otherProps
}: FileUploadProps) {
  const [file, setFile] = useState<
    File | BuktiKaryaDocumentResponse | undefined
  >()
  const [isSourceUploadDialogOpen, setIsSourceUploadDialogOpen] =
    useState(false)

  const isBuktiDukungAlreadySubmitted =
    realizationState === 'EVALUATED' ||
    realizationState === 'WAITING_EVALUATION'

  const validateFile = (file: File) => {
    const validateSize = file.size <= 10 * 1024 * 1024

    return validateSize
  }

  const handleFileSelected = (file: File | BuktiKaryaDocumentResponse) => {
    if (file instanceof File) {
      if (validateFile(file)) {
        setFile(file)
        onChange(file)
      } else {
        onError('Ukuran dokumen lebih dari 10 MB')
      }
    } else {
      setFile(file)
      onChange(file)
    }
  }

  let name: string

  if (file instanceof File) {
    name = file.name
  } else {
    name = file?.title || buktiDukungFile.fileName || buktiDukungFile.fileUrl
  }

  const hasUploadedFile = Boolean(file || buktiDukungFile.fileUrl)

  const [isPDFViewerOpen, setIsPDFViewerOpen] = useState(false)

  let blobUrl: string

  if (file instanceof File) {
    blobUrl = URL.createObjectURL(file)
  } else {
    blobUrl = file?.fileUrl ?? buktiDukungFile.fileUrl
  }

  return (
    <>
      <div className="flex flex-col gap-1" {...otherProps}>
        <Text
          variant="subheading"
          className="uppercase tracking-[0.5px] overflow-wrap-anywhere"
        >
          {description}
        </Text>
        <div className="py-3 pl-3 pr-4 flex items-center rounded border border-dashed border-[#808489] gap-2 max-w-[736px]">
          {hasUploadedFile ? (
            <>
              {isUploading ? (
                <Spinner size="sm" />
              ) : (
                <Icon className="text-[#52575C]">text_snippet</Icon>
              )}
              <Text className="flex-1 truncate" title={name}>
                {name}
              </Text>
            </>
          ) : (
            <>
              <Icon className="text-[#52575C] rotate-45">attach_file</Icon>
              <Text color="subdued" className="flex-1">
                Belum ada dokumen.
              </Text>
            </>
          )}
          {hasUploadedFile || isBuktiDukungAlreadySubmitted ? (
            <Button
              color="white"
              size="lg"
              onClick={() => {
                setIsPDFViewerOpen(true)
                onOpenFile?.()
              }}
              disabled={isUploading}
            >
              Cek
            </Button>
          ) : (
            <Button
              color="black"
              size="lg"
              disabled={isDisabled}
              onClick={() => setIsSourceUploadDialogOpen(true)}
            >
              Unggah
            </Button>
          )}
          <Button
            color="white"
            size="lg"
            disabled={!hasUploadedFile || isUploading || isDisabled}
            onClick={() => setIsSourceUploadDialogOpen(true)}
          >
            Ubah
          </Button>
        </div>
      </div>
      <SourceUploadDialog
        isOpen={isSourceUploadDialogOpen}
        onClose={() => setIsSourceUploadDialogOpen(false)}
        onChange={handleFileSelected}
      />

      {hasUploadedFile && (
        <PDFViewerDialog
          url={blobUrl}
          name={name}
          isOpen={isPDFViewerOpen}
          onClose={() => setIsPDFViewerOpen(false)}
        />
      )}
    </>
  )
}
